import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiRequest } from "@/lib/queryClient"
import { useToast } from "@/hooks/use-toast"
import { CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FloatingCard } from "@/components/FloatingCard"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Film, Loader2, AlertCircle, Trash2 } from "lucide-react"
import { videoCardState, formatDuration, type LibraryVideo } from "@/lib/hotelVideoCardState"

// Hotel video section of the Post-Bibliothek (PLAN.md §23). Rendered only for hotels and only when the hotel has
// videos, so it changes nothing until the first video exists. Signed URLs live 1 h; the list refetches well
// before that, and every 30 s while a video is still being processed.
const QUERY_KEY = ["/api/content/videos"]

export function HotelVideoLibrary({ enabled }: { enabled: boolean }) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [confirm, setConfirm] = useState<LibraryVideo | null>(null)
  const { data } = useQuery<{ videos: LibraryVideo[] }>({
    queryKey: QUERY_KEY,
    enabled,
    staleTime: 5 * 60_000,
    refetchInterval: (q) => {
      const videos = (q.state.data as { videos: LibraryVideo[] } | undefined)?.videos ?? []
      return videos.some((v) => videoCardState(v).busy) ? 30_000 : 30 * 60_000
    },
  })
  const del = useMutation({
    mutationFn: async (id: string) => { await apiRequest("DELETE", `/api/content/videos/${id}`) },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      toast({ title: "Gelöscht", description: "Das Video wurde gelöscht." })
      setConfirm(null)
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message || "Löschen fehlgeschlagen.", variant: "destructive" })
    },
  })

  const videos = data?.videos ?? []
  if (!enabled || videos.length === 0) return null

  return (
    <section aria-labelledby="hotel-videos-heading" className="space-y-4" data-testid="hotel-video-library">
      <h2 id="hotel-videos-heading" className="text-2xl font-semibold flex items-center gap-2">
        <Film className="h-6 w-6 text-primary" aria-hidden="true" />
        Ihre Videos
      </h2>
      <p className="text-muted-foreground">Ihr Team baut daraus Reels. Videos werden nicht automatisch gepostet.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {videos.map((v) => {
          const state = videoCardState(v)
          return (
            <FloatingCard key={v.id}>
              <CardContent className="p-0">
                <div className="aspect-[9/16] max-h-96 w-full bg-muted flex items-center justify-center overflow-hidden rounded-t-xl">
                  {state.playable && v.videoUrl ? (
                    <video
                      src={v.videoUrl}
                      poster={v.posterUrl ?? undefined}
                      controls
                      playsInline
                      preload="none"
                      className="h-full w-full object-contain bg-black"
                      data-testid={`video-player-${v.id}`}
                    />
                  ) : state.busy ? (
                    <div className="text-center text-muted-foreground space-y-2 p-4">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto" aria-hidden="true" />
                      <p className="text-sm">{state.label}</p>
                    </div>
                  ) : (
                    <div className="text-center text-muted-foreground space-y-2 p-4">
                      <AlertCircle className="h-8 w-8 mx-auto text-destructive" aria-hidden="true" />
                      <p className="text-sm">{state.hint}</p>
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant={state.tone === "error" ? "destructive" : state.tone === "ok" ? "default" : "secondary"}>{state.label}</Badge>
                    {v.durationMs ? <span className="text-xs text-muted-foreground">{formatDuration(v.durationMs)}</span> : null}
                  </div>
                  {v.pillarName && <p className="text-sm font-medium">{v.pillarName}</p>}
                  {v.message && <p className="text-sm text-muted-foreground line-clamp-3">{v.message}</p>}
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setConfirm(v)} aria-label={`Video ${v.originalName} löschen`} data-testid={`video-delete-${v.id}`}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </FloatingCard>
          )
        })}
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Video „{confirm?.originalName}" löschen?</AlertDialogTitle>
            <AlertDialogDescription>Das Video und das Original werden gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirm && del.mutate(confirm.id)}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? "Löschen..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
